import { useMemo, useState } from "react";
import { toast } from "sonner";
import "./admin.css";
import { useAdminGrants } from "@/hooks/queries/useAdminGrants";
import type { Grant } from "@/hooks/queries/useAdminGrants";
import { useAdminUserSearch } from "@/hooks/queries/useAdminUserSearch";
import type { UserRow } from "@/hooks/queries/useAdminUserSearch";
import { useAdminUserBilling } from "@/hooks/queries/useAdminUserBilling";
import { useGrantTier, useRevokeGrant } from "@/hooks/queries/useAdminGrantMutations";

const TIER_OPTIONS = [
  { id: "ember", label: "Ember" },
  { id: "keeper", label: "Keeper" },
  { id: "storyteller", label: "Storyteller" },
];

const TIER_LABELS: Record<string, string> = {
  seedling: "Seedling",
  ember: "Ember",
  keeper: "Keeper",
  storyteller: "Storyteller",
};

// Tier ordering for "is comping a tier ≤ their current paid tier?" check.
// Seedling is lowest, Storyteller highest. Only tiers a paying user might
// already be on need a rank; unknown slugs get -1 so they never trigger the
// warning.
const TIER_RANK: Record<string, number> = {
  seedling: 0,
  ember: 1,
  keeper: 2,
  storyteller: 3,
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatExpiry(iso: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diffMs = d.getTime() - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const abs = Math.abs(diffDays);
  const unit = abs === 1 ? "day" : "days";
  const when = diffMs > 0 ? `in ${abs} ${unit}` : `${abs} ${unit} ago`;
  return `${formatDateTime(iso)} (${when})`;
}

export default function GrantsManager() {
  const [showUsed, setShowUsed] = useState(false);

  // ── Grant form state ──
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [tierId, setTierId] = useState<string>("keeper");
  const [expiryDays, setExpiryDays] = useState<string>("14");
  const [isPermanent, setIsPermanent] = useState(false);
  const [pauseStripe, setPauseStripe] = useState(false);
  const [note, setNote] = useState("");

  const { grants, isLoading, isFetching, error, refetch } = useAdminGrants();
  const { data: userResults } = useAdminUserSearch(userSearch);
  const { billing: selectedBilling } = useAdminUserBilling(
    selectedUser?.id ?? null,
  );
  const grantTier = useGrantTier();
  const revokeGrant = useRevokeGrant();

  const hasActiveStripe =
    !!selectedBilling?.stripe_subscription_id &&
    selectedBilling?.status === "active";
  const currentPaidRank =
    hasActiveStripe && selectedBilling
      ? (TIER_RANK[selectedBilling.tier_id] ?? -1)
      : -1;
  const grantingRank = TIER_RANK[tierId] ?? -1;
  const sameOrLowerTierThanPaid =
    hasActiveStripe && grantingRank <= currentPaidRank;

  const filteredGrants = useMemo(
    () => grants.filter((g) => showUsed || !g.used),
    [grants, showUsed],
  );

  const resetForm = () => {
    setUserSearch("");
    setSelectedUser(null);
    setTierId("keeper");
    setExpiryDays("14");
    setIsPermanent(false);
    setPauseStripe(false);
    setNote("");
  };

  const handleGrant = async () => {
    if (!selectedUser) {
      toast.error("Pick a user first");
      return;
    }
    let expiresAtIso: string | null = null;
    if (!isPermanent) {
      const days = Number(expiryDays);
      if (!Number.isFinite(days) || days <= 0) {
        toast.error("Expiry must be a positive number of days");
        return;
      }
      expiresAtIso = new Date(
        Date.now() + days * 24 * 60 * 60 * 1000,
      ).toISOString();
    }

    // Confirm before comping a tier ≤ what the user already pays for
    // without pausing Stripe — otherwise they'd be double-billed.
    if (sameOrLowerTierThanPaid && !pauseStripe) {
      const ok = confirm(
        `${selectedUser.email} is already paying Stripe for ${
          TIER_LABELS[selectedBilling!.tier_id] ?? selectedBilling!.tier_id
        }. ` +
          `Granting ${TIER_LABELS[tierId] ?? tierId} without pausing billing means they'll keep being charged. Continue anyway?`,
      );
      if (!ok) return;
    }

    try {
      await grantTier.mutateAsync({
        user_id: selectedUser.id,
        tier_id: tierId,
        expires_at: expiresAtIso,
        note: note.trim() || null,
        pause_stripe: pauseStripe && hasActiveStripe,
      });
      toast.success(
        `Granted ${TIER_LABELS[tierId] ?? tierId} to ${selectedUser.email}` +
          (isPermanent ? " (permanent)" : ` for ${expiryDays} days`),
      );
      resetForm();
    } catch (err) {
      toast.error(`Grant failed: ${(err as Error).message}`);
    }
  };

  const handleRevoke = async (grant: Grant) => {
    if (
      !confirm(
        `Revoke ${TIER_LABELS[grant.tier_id] ?? grant.tier_id} grant for ${
          grant.user_email ?? grant.user_id
        }? They'll be restored to the tier they had before this grant (or Seedling if they had none).`,
      )
    ) {
      return;
    }

    try {
      const data = await revokeGrant.mutateAsync({
        grant_id: grant.id,
        user_id: grant.user_id,
      });
      const restoredTier =
        (data as { to_tier_id?: string } | null)?.to_tier_id ?? "seedling";
      toast.success(
        `Revoked — ${grant.user_email ?? grant.user_id} is now ${
          TIER_LABELS[restoredTier] ?? restoredTier
        }`,
      );
    } catch (err) {
      toast.error(`Revoke failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-gray-900">Grants</h1>
        <button
          onClick={() => void refetch()}
          disabled={isFetching}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {isFetching ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* ── Grant form ────────────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-gray-200 p-5 mb-8">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">
          Grant a free comp
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* User picker */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              User (email, nickname, or id)
            </label>
            {selectedUser ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {selectedUser.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {selectedUser.nickname ?? "—"} · currently{" "}
                    {TIER_LABELS[selectedUser.tier_id] ?? selectedUser.tier_id}
                    {hasActiveStripe && (
                      <>
                        {" "}
                        · <span className="text-gray-700">paying via Stripe</span>
                        {selectedBilling?.pause_collection_set && (
                          <> <span className="text-amber-700">(billing paused by active comp)</span></>
                        )}
                      </>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setUserSearch("");
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Type to search (min 2 chars)"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                {userResults.length > 0 && (
                  <div className="mt-2 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white">
                    {userResults.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setSelectedUser(u);
                        }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 border-gray-100"
                      >
                        <span className="font-medium text-gray-900">
                          {u.email}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {u.nickname ?? "—"} ·{" "}
                          {TIER_LABELS[u.tier_id] ?? u.tier_id}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Tier */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tier
            </label>
            <select
              className="admin-input"
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Duration
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                className="admin-input"
                value={expiryDays}
                disabled={isPermanent}
                onChange={(e) => setExpiryDays(e.target.value)}
                style={{ width: "6rem" }}
              />
              <span className="text-sm text-gray-600">days</span>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 ml-4">
                <input
                  type="checkbox"
                  checked={isPermanent}
                  onChange={(e) => setIsPermanent(e.target.checked)}
                />
                Permanent
              </label>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              className="admin-input"
              placeholder="e.g. investor comp, Q1 partner"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* Pause Stripe during comp — only meaningful if user has an active
              Stripe sub. When unchecked and gifting ≤ paid tier, handleGrant
              warns before submitting. */}
          <div className="md:col-span-2">
            <label
              className={`flex items-start gap-2 text-sm ${
                hasActiveStripe ? "text-gray-700" : "text-gray-400"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={pauseStripe && hasActiveStripe}
                disabled={!hasActiveStripe}
                onChange={(e) => setPauseStripe(e.target.checked)}
              />
              <span>
                Pause Stripe billing during this comp
                <span className="block text-xs text-gray-500 font-normal">
                  {hasActiveStripe
                    ? "Customer won't be charged until the comp ends (revoke or expiry). Billing resumes automatically."
                    : "Only available when the user has an active Stripe subscription."}
                </span>
              </span>
            </label>

            {sameOrLowerTierThanPaid && !pauseStripe && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Heads up: this user already pays Stripe for{" "}
                {TIER_LABELS[selectedBilling!.tier_id] ?? selectedBilling!.tier_id}
                . Granting {TIER_LABELS[tierId] ?? tierId} without pausing means
                they keep being charged during the comp.
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={handleGrant}
            disabled={grantTier.isPending || !selectedUser}
            className="px-4 py-2 text-sm font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {grantTier.isPending ? "Granting..." : "Grant tier"}
          </button>
          {selectedUser && (
            <button
              onClick={resetForm}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
      </section>

      {/* ── Grants list ────────────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">
            Admin comps ({filteredGrants.length})
          </h2>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showUsed}
              onChange={(e) => setShowUsed(e.target.checked)}
            />
            Show revoked / expired
          </label>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border-b border-red-100">
            <p className="text-sm text-red-700">Failed to load: {error.message}</p>
          </div>
        )}

        {isLoading && grants.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">Loading...</div>
        ) : filteredGrants.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">No grants yet.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Tier</th>
                <th>Granted</th>
                <th>Expires</th>
                <th>Granted by</th>
                <th>Status</th>
                <th style={{ width: "1%" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredGrants.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div className="text-gray-900 font-medium">
                      {g.user_email ?? "—"}
                    </div>
                    <div className="text-xs text-gray-400 font-mono">
                      {g.user_id.slice(0, 8)}
                    </div>
                  </td>
                  <td className="text-gray-700">
                    {TIER_LABELS[g.tier_id] ?? g.tier_id}
                  </td>
                  <td className="text-gray-500 text-xs">
                    {formatDateTime(g.granted_at)}
                  </td>
                  <td className="text-gray-500 text-xs">
                    {formatExpiry(g.expires_at)}
                  </td>
                  <td className="text-gray-500 text-xs">
                    {g.granted_by_email ?? "—"}
                  </td>
                  <td>
                    {g.used ? (
                      <span className="text-xs text-gray-500">Inactive</span>
                    ) : (
                      <span className="text-xs text-green-700 font-medium">
                        Active
                      </span>
                    )}
                  </td>
                  <td>
                    {!g.used && (
                      <button
                        onClick={() => handleRevoke(g)}
                        className="text-xs px-2 py-1 rounded border border-gray-200 text-red-600 hover:bg-red-50"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
